import React, { useEffect, useRef, useState } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Card, CardBody, Col } from 'reactstrap';
import Flatpickr from 'react-flatpickr';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { GET_CARD_SUMMARY, GET_FILTERS_LIST } from '../../../../store/actions';
import CountUp from 'react-countup';
import { Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface RootState {
  GetFiltersList?: {
    data?: any;
    loading?: boolean;
  };
}

const DonutChartFilter = ({ cardDetails, cardData }) => {
  const dispatch = useDispatch();

  console.log('cardDetails', cardDetails);

  const [filter, setFilter] = useState({ start_date: moment().subtract(7, 'days').toISOString(), end_date: moment().toISOString(), region: null });
  const flatpickrRef = useRef<any>(null);
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    if (cardDetails) {
      dispatch({
        type: GET_CARD_SUMMARY,
        payload: {
          cards: [cardDetails],
          ...filter,
        },
      });
    }
  }, [cardDetails, filter]);

  useEffect(() => {
    if (cardData) {
      setSelectedItems(cardData?.[cardDetails?.name]?.data?.data?.distributions ?? []);
    }
  }, [cardData]);

  const toggleLegend = item => {
    setSelectedItems(prev => {
      const isRemoved = prev?.some(d => d?.name === item.name);
      return isRemoved ? prev?.filter(d => d?.name !== item.name) : [...prev, item];
    });
  };

  const pieData = {
    labels: selectedItems.map(item => item.name),
    datasets: [
      {
        data: selectedItems.map(item => item.value),
        backgroundColor: selectedItems.map(item => item.color),
        borderWidth: 1,
        cutout: '65%',
      },
    ],
  };

  const updatedTotalCount = selectedItems.reduce((sum, item) => sum + item.value, 0);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        color: 'white',
        font: {
          weight: 'normal',
        },
      },
    },
  };

  useEffect(() => {
    if (flatpickrRef.current) {
      const flatpickrInstance = flatpickrRef.current.flatpickr;

      flatpickrInstance.config.onOpen.push(() => {
        const calendarContainer = flatpickrInstance.calendarContainer;

        if (!calendarContainer.querySelector('.custom-options')) {
          const customOptions = document.createElement('div');
          customOptions.className = 'custom-options';
          Object.assign(customOptions.style, {
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            margin: '0', // Remove extra spacing
            padding: '10px',
            backgroundColor: '#FFFFFF', // Background for better visibility
            position: 'absolute', // Allow positioning next to the calendar
            top: '0', // Align with the calendar's top
            right: 'calc(100% + 1px)', // Position it to the right of the calendar
            zIndex: '9999', // Ensure it appears above other elements
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: '5px',
          });

          const options = ['Today', 'Last 7 Days', 'Last 14 Days', 'Custom Range'];

          options.forEach(option => {
            const button = document.createElement('button');
            Object.assign(button.style, {
              padding: '11px 10px',
              fontSize: '0.8rem',
              margin: '10px 0',
              backgroundColor: '#3F5189',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              width: '122px',
            });

            button.textContent = option;

            button.onmouseenter = () => (button.style.backgroundColor = '#5A6FA1');
            button.onmouseleave = () => (button.style.backgroundColor = '#3F5189');

            button.addEventListener('click', () => handleCustomRange(option, flatpickrInstance));
            customOptions.appendChild(button);
          });

          calendarContainer.parentNode.style.position = 'relative'; // Ensure parent allows positioning
          calendarContainer.appendChild(customOptions);
        }
      });
    }
  }, []);

  const handleCustomRange = (option, flatpickrInstance) => {
    let start_date = null;
    let end_date = null;

    switch (option) {
      case 'Today':
        start_date = end_date = new Date();
        break;
      case 'Last 7 Days':
        start_date = new Date(new Date().setDate(new Date().getDate() - 7));
        end_date = new Date();
        break;
      case 'Last 14 Days':
        start_date = new Date(new Date().setDate(new Date().getDate() - 14));
        end_date = new Date();
        break;
      default:
        return;
    }
    // Set selected range and close calendar
    let updatedState = null;
    updatedState = {
      ...filter,
      start_date: moment(start_date).toISOString(),
      end_date: moment(end_date).toISOString(),
    };
    setFilter(updatedState);
    flatpickrInstance.close();
    // const filtersObject = appliedFilters.reduce((acc, curr) => {
    //   acc[curr.key] = curr.value;
    //   return acc;
    // }, {});
    // props?.filterHandler({ ...updatedState, ...filtersObject });
  };

  const datePickHandler = (flag, date) => {
    let updatedState = null;
    if (date[0]) {
      const start = new Date(date[0]);
      const maxRange = new Date(start);
      maxRange.setDate(start.getDate() + 60);

      // Set the Flatpickr range restrictions dynamically
      flatpickrRef.current.flatpickr.set('minDate', start);
      flatpickrRef.current.flatpickr.set('maxDate', maxRange);
    }
    switch (flag) {
      case 'date':
        if (date[0] && date[1]) {
          updatedState = {
            ...filter,
            start_date: moment(new Date(date[0])).toISOString(),
            end_date: moment(new Date(date[1])).toISOString(),
          };
          setFilter(updatedState);
        }
        break;
    }
  };

  return (
    <Col xl={6} md={12} lg={6} className="mb-2" style={{ borderRadius: '10px' }}>
      <Card>
        <div className="d-flex justify-content-between m-4">
          <h4 className="card-title mb-0 flex-grow-1 fw-semibold">System Preferred Vs Other Vendor Ratio Chart</h4>
          <div className="d-flex gap-2 align-items-center">
            <Flatpickr
              ref={flatpickrRef}
              className="form-control"
              onChange={date => datePickHandler(`date`, date)}
              value={filter?.start_date && filter?.end_date ? [new Date(filter.start_date), new Date(filter.end_date)] : []}
              placeholder="Select Range"
              options={{
                mode: 'range',
                dateFormat: 'Y-m-d',
              }}
              style={{ width: '201px' }}
            />
          </div>
        </div>

        <CardBody style={{ maxHeight: '420px', position: 'relative', textAlign: 'center', height: '402px' }}>
          <div style={{ height: '300px', position: 'relative' }}>
            <Doughnut data={pieData} options={options} />

            {/* Total in center */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '18px',
                fontWeight: '500',
              }}
            >
              {/* <div style={{ fontSize: '14px', fontWeight: 'normal', color: '#8E8E93' }}>{data?.countsDisplayHeading}</div> */}
              <CountUp separator="" start={0} end={Number(updatedTotalCount)} duration={2} style={{ fontSize: '25px' }} className="fw-bold" />
            </div>
          </div>

          {/* Custom Legends */}
          <div className="d-flex flex-wrap justify-content-center mt-3">
            {cardData?.[cardDetails?.name]?.data?.data?.distributions?.map(item => {
              const isSelected = selectedItems.some(d => d.name === item.name);
              return (
                <div
                  key={item.name}
                  onClick={() => toggleLegend(item)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    margin: '3px',
                    padding: '5px 10px',
                    backgroundColor: '#F2F2F7',
                    color: 'black',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  <div
                    style={{
                      width: '13px',
                      height: '13px',
                      backgroundColor: item.color,
                      borderRadius: '3px',
                      marginRight: '8px',
                      border: '1px solid #ccc',
                    }}
                  ></div>
                  <span
                    style={{
                      textDecoration: !isSelected ? 'line-through' : 'none',
                      fontWeight: '400',
                      fontSize: 12,
                    }}
                  >
                    {item.name}
                  </span>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

export default DonutChartFilter;
